import * as React from 'react';
import { ArrowDownRight, ArrowUpRight, Minus } from 'lucide-react';

import { cn } from '@/lib/utils';
import { SoftCard } from '@/components/ui/soft-card';

/**
 * Big number + label + trend pill. Drives the "Views / Engagements / Orders"
 * row on the Overview, plus the headline KPIs on Results, Ads, Customers, etc.
 *
 * `trend` is a percentage; positive renders as success, negative as destructive,
 * exact zero as neutral. Pass `formatValue` to override Intl number formatting.
 */
const defaultFormatter = (value) => {
	if (value === null || value === undefined) return '—';
	if (typeof value !== 'number') return String(value);
	return new Intl.NumberFormat('en-GB').format(value);
};

const KPICard = React.forwardRef(({ className, label, value, trend, suffix, helpText, formatValue = defaultFormatter, ...props }, ref) => {
	const trendNum = typeof trend === 'number' ? trend : null;
	const trendDirection = trendNum === null ? null : trendNum > 0 ? 'up' : trendNum < 0 ? 'down' : 'flat';
	const TrendIcon = trendDirection === 'up' ? ArrowUpRight : trendDirection === 'down' ? ArrowDownRight : Minus;
	const trendClass =
		trendDirection === 'up'
			? 'bg-success-soft text-success-soft-foreground'
			: trendDirection === 'down'
				? 'bg-destructive/10 text-destructive'
				: 'bg-secondary text-secondary-foreground';

	return (
		<SoftCard ref={ref} tone="white" padding="md" className={cn('flex flex-col gap-3', className)} {...props}>
			<div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</div>
			<div className="flex items-baseline gap-2">
				<div className="text-3xl font-semibold leading-none text-foreground tabular-nums">{formatValue(value)}</div>
				{suffix ? <div className="text-sm font-medium text-muted-foreground">{suffix}</div> : null}
			</div>
			<div className="flex items-center justify-between gap-2">
				{trendDirection ? (
					<span className={cn('inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold', trendClass)}>
						<TrendIcon className="h-3 w-3" />
						{Math.abs(trendNum).toFixed(1)}%
					</span>
				) : (
					<span />
				)}
				{helpText ? <span className="text-xs text-muted-foreground">{helpText}</span> : null}
			</div>
		</SoftCard>
	);
});
KPICard.displayName = 'KPICard';

export { KPICard };
